import React, { useEffect, useMemo, useRef, useState } from "react";
import { priceOff } from "@/components/Cards";
import { MicRow, appendText } from "@/components/VoiceMic";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { vendorCreateProduct, vendorUpdateProduct } from "@/lib/vendorApi";
import { uploadImage, resolveImageUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload, X, Star, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import MultiImageInput from "@/components/MultiImageInput";

const EMPTY = (shops) => ({
  name: "", hindi: "", price: "", mrp: "", unit: "", image: "", special_note: "",
  images: [], image_captions: {},
  category: "grocery", description: "", shop_id: shops[0]?.shop_id || "", in_stock: true,
});

function PriceOffPreview({ mrp, price }) {
  if (mrp === "" || price === "" || !(Number(mrp) > 0)) return null;
  const off = priceOff(mrp, price);
  return (
    <div className={`col-span-2 -mt-1 text-xs font-semibold ${off ? "text-emerald-700" : "text-slate-400"}`} data-testid="new-product-discount-preview">
      {off ? `${off.pct}% OFF · Save ₹${off.save}` : "Discount tabhi dikhega jab Price, MRP se kam ho"}
    </div>
  );
}

export default function AddProductDialog({ open, onOpenChange, shops, product, onCreated, onSaved }) {
  const { formatApiError, refreshUser, isVendor } = useApp();
  const isEdit = !!product?.id;
  const [form, setForm] = useState(EMPTY(shops));
  const productCats = useMemo(() => [form.category], [form.category]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    if (product) {
      const imgs = product.images && product.images.length ? [...product.images] : (product.image ? [product.image] : []);
      setForm({
        name: product.name || "",
        hindi: product.hindi || "",
        price: product.price ?? "",
        mrp: product.mrp ?? "",
        special_note: product.special_note || "",
        unit: product.unit || "",
        image: imgs[0] || "",
        images: imgs,
        image_captions: { ...(product.image_captions || {}) },
        category: product.category || "grocery",
        description: product.description || "",
        shop_id: product.shop_id || shops[0]?.shop_id || "",
        in_stock: product.in_stock !== false,
      });
    } else {
      setForm(EMPTY(shops));
    }
  }, [open, product?.id]);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploads = [];
      for (const file of Array.from(files)) {
        if (!file.type.startsWith("image/")) continue;
        try {
          const res = await uploadImage(file);
          uploads.push(res.url);
        } catch (err) {
          toast.error(`Upload failed for ${file.name}: ${formatApiError(err)}`);
        }
      }
      if (uploads.length) {
        setForm((f) => {
          const next = [...(f.images || []), ...uploads];
          return { ...f, images: next, image: f.image || next[0] };
        });
        toast.success(`${uploads.length} image${uploads.length === 1 ? "" : "s"} uploaded`);
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addUrl = () => {
    const url = prompt("Paste image URL:");
    if (!url || !url.trim()) return;
    setForm((f) => {
      const next = [...(f.images || []), url.trim()];
      return { ...f, images: next, image: f.image || next[0] };
    });
  };

  const removeImage = (idx) => {
    setForm((f) => {
      const next = (f.images || []).filter((_, i) => i !== idx);
      return { ...f, images: next, image: next[0] || "" };
    });
  };

  const moveImage = (idx, dir) => {
    setForm((f) => {
      const imgs = [...(f.images || [])];
      const j = idx + dir;
      if (j < 0 || j >= imgs.length) return f;
      [imgs[idx], imgs[j]] = [imgs[j], imgs[idx]];
      return { ...f, images: imgs, image: imgs[0] };
    });
  };

  const setCover = (idx) => {
    setForm((f) => {
      const imgs = [...(f.images || [])];
      if (idx <= 0 || idx >= imgs.length) return f;
      const [chosen] = imgs.splice(idx, 1);
      imgs.unshift(chosen);
      return { ...f, images: imgs, image: imgs[0] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const imgs = (form.images || []).filter(Boolean);
      const payload = {
        ...form,
        price: parseFloat(form.price) || 0,
        mrp: parseFloat(form.mrp) || 0,
        special_note: (form.special_note || "").trim().slice(0, 30),
        shop_id: form.shop_id || shops[0]?.shop_id,
        images: imgs,
        image: imgs[0] || form.image || "",
        image_captions: Object.fromEntries(Object.entries(form.image_captions || {}).map(([k, v]) => [k, (v || "").trim().replace(/\s+/g, " ").slice(0, 60)]).filter(([k, v]) => v && imgs.includes(k))),
      };
      if (isEdit) {
        await vendorUpdateProduct(product.id, payload);
        toast.success("Product updated");
        onSaved?.();
      } else {
        await vendorCreateProduct(payload);
        toast.success("Product added");
        // First-ever product grants the "vendor" role server-side.
        // Refresh the cached user so Vendor Mode UI appears immediately.
        if (!isVendor) {
          try { await refreshUser(); } catch { /* product already saved; Vendor Mode appears on next session check */ }
        }
        onCreated?.();
      }
      onOpenChange(false);
      setForm(EMPTY(shops));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:w-full sm:max-w-lg max-h-[92vh] overflow-y-auto p-4 sm:p-6" data-testid="add-product-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{isEdit ? "Edit product" : "Add new product"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Shop</label>
            <select value={form.shop_id} onChange={(e) => set("shop_id", e.target.value)} className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500" data-testid="new-product-shop">
              {shops.map((s) => <option key={s.shop_id} value={s.shop_id}>{s.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Product name*</label>
              <input value={form.name} onChange={(e) => set("name", e.target.value)} required className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500" data-testid="new-product-name" />
              <MicRow onText={(t) => set("name", appendText(form.name, t))} testid="mic-product-name" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Price (₹)*</label>
              <input value={form.price} onChange={(e) => set("price", e.target.value)} required type="number" min="0" className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500" data-testid="new-product-price" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">MRP (₹)</label>
              <input value={form.mrp} onChange={(e) => set("mrp", e.target.value)} type="number" min="0" className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500" data-testid="new-product-mrp" />
            </div>
            <PriceOffPreview mrp={form.mrp} price={form.price} />
            <div className="col-span-2">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Special Note <span className="normal-case font-normal text-slate-400">(optional, photo pe badge)</span></label>
              <input value={form.special_note} onChange={(e) => set("special_note", e.target.value.slice(0, 30))} maxLength={30} placeholder="e.g. 20% OFF, Limited Edition, Hurry!, Festival Offer" className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500" data-testid="new-product-special-note" />
              <div className="text-[10px] text-slate-400 text-right mt-0.5" data-testid="new-product-special-note-count">{(form.special_note || "").length}/30</div>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Unit</label>
              <input value={form.unit} onChange={(e) => set("unit", e.target.value)} placeholder="e.g. 1 kg" className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500" data-testid="new-product-unit" />
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Category</label>
              <input value={form.category} onChange={(e) => set("category", e.target.value)} className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500" data-testid="new-product-category" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Description</label>
              <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className="w-full mt-1 border border-slate-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-emerald-500" data-testid="new-product-description" />
              <MicRow onText={(t) => set("description", appendText(form.description, t))} testid="mic-product-description" />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1">Availability</label>
              <div className="flex items-center gap-3 border border-slate-200 rounded-xl px-3 py-2.5">
                <button
                  type="button"
                  onClick={() => set("in_stock", !form.in_stock)}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none ${form.in_stock ? "bg-emerald-600" : "bg-slate-300"}`}
                  aria-label="Toggle availability"
                  aria-pressed={!!form.in_stock}
                  data-testid="new-product-availability"
                >
                  <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ${form.in_stock ? "translate-x-5" : "translate-x-0"}`} />
                </button>
                <div className="min-w-0">
                  <div className={`text-sm font-semibold ${form.in_stock ? "text-emerald-700" : "text-slate-700"}`}>{form.in_stock ? "Available" : "Unavailable"}</div>
                  <div className="text-[11px] text-slate-500">Customers can {form.in_stock ? "order this item" : "still see this item but cannot order"}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Multi-image gallery editor — Camera + Gallery + URL */}
          <MultiImageInput
            label={`Product images (${(form.images || []).length} added)`}
            value={form.images || []}
            onChange={(next) => setForm((f) => ({ ...f, images: next, image: next[0] || "" }))}
            testid="product-images"
            captions={form.image_captions || {}}
            onCaptionsChange={(c) => set("image_captions", c)}
            libraryCategory="products"
            librarySuggest={productCats}
          />

          <button type="submit" disabled={saving} className="w-full bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white rounded-xl py-3 text-sm font-semibold" data-testid="new-product-submit">
            {saving ? "Saving..." : (isEdit ? "Save changes" : "Add product")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
